import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Home,
  Building2,
  MapPin,
  Plus,
  ChevronRight,
  AlertCircle,
  Clock,
  IndianRupee,
  CheckCircle,
} from "lucide-react";
import API_BASE_URL from "../../../services/api";

const TODAY = new Date().toISOString().split("T")[0];

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const SampleCollectionSection = ({
  formData,
  onFieldChange,
  patientAddresses = [],
  onOpenAddressSelect,
  onOpenNewAddress,
  loading = false,
  collectionCenters = [],
  // ── NEW props ──
  baseTotal = 0,
  onCollectionChargeChange,
}) => {
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [homeSlots, setHomeSlots] = useState([]);

  // ── Change 1: Home collection charge ──
  const [collectionPriceConfig, setCollectionPriceConfig] = useState(null); // { price, minAmount }
  const [collectionPriceLoading, setCollectionPriceLoading] = useState(false);

  // ── Change 2: B2B center slots ──
  const [centerSlots, setCenterSlots] = useState([]);
  const [centerSlotsLoading, setCenterSlotsLoading] = useState(false);
  // B2B visit date — lifted to formData so parent can include it in the payload
  const centerVisitDate = formData.centerVisitDate || TODAY;

  /* ───────── helpers ───────── */
  const formatAddress = (a) =>
    `${a.address || ""}, ${a.landmark || ""}, ${a.city?.name || a.city || ""}, ${
      a.state || ""
    } - ${a.pincode || ""}`
      .replace(/,\s*,/g, ",")
      .trim();

  const selectedAddress = useMemo(
    () =>
      patientAddresses.find(
        (a) => String(a.id) === String(formData.selectedAddressId),
      ),
    [patientAddresses, formData.selectedAddressId],
  );

  const activeHomeSlots = useMemo(
    () => (homeSlots || []).filter((s) => s.isFull === false),
    [homeSlots],
  );

  const availableCenters = useMemo(
    () => (Array.isArray(collectionCenters) ? collectionCenters : []),
    [collectionCenters],
  );

  /* ───────── CHANGE 1 – fetch collection prices & pick best match ───────── */
  useEffect(() => {
    const fetchCollectionPrice = async () => {
      if (!formData.homeCollection) {
        setCollectionPriceConfig(null);
        return;
      }

      try {
        setCollectionPriceLoading(true);

        // Fetch all active collection price rules once
        const res = await fetch(
          `${API_BASE_URL}/api/collection-prices`,
          { headers: authHeaders() },
        );

        if (!res.ok) {
          setCollectionPriceConfig(null);
          return;
        }

        const json = await res.json().catch(() => null);
        const allPrices = Array.isArray(json?.collectionPrices)
          ? json.collectionPrices
          : Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];

        const active = allPrices.filter((p) => p.isActive !== false);
        if (active.length === 0) {
          setCollectionPriceConfig(null);
          return;
        }

        // Address details for matching
        const pincode = selectedAddress?.pincode || null;
        const cityName = (
          selectedAddress?.city?.name ||
          selectedAddress?.city ||
          selectedAddress?.cityName ||
          ""
        ).toLowerCase();

        // Priority: pincode match > city match > global (all nulls)
        let matched = null;

        // 1. Pincode exact match
        if (pincode) {
          matched = active.find(
            (p) => p.pincode && String(p.pincode).trim() === String(pincode).trim(),
          ) || null;
        }

        // 2. City name match
        if (!matched && cityName) {
          matched = active.find(
            (p) =>
              !p.pincode &&
              p.city &&
              p.city.name &&
              p.city.name.toLowerCase() === cityName,
          ) || null;
        }

        // 3. Global fallback: all location fields null
        if (!matched) {
          matched = active.find(
            (p) => p.pincode == null && p.cityId == null && p.centerId == null,
          ) || null;
        }

        if (matched) {
          setCollectionPriceConfig({
            price: Number(matched.price),
            minAmount: Number(matched.minAmount ?? 0),
          });
        } else {
          setCollectionPriceConfig(null);
        }
      } catch (err) {
        console.error("Collection price fetch error:", err);
        setCollectionPriceConfig(null);
      } finally {
        setCollectionPriceLoading(false);
      }
    };

    fetchCollectionPrice();
  }, [formData.homeCollection, formData.selectedAddressId, selectedAddress]);

  /* ───────── CHANGE 1 – calculate & emit collection charge ───────── */
  const collectionCharge = useMemo(() => {
    if (!formData.homeCollection) return 0;
    if (!collectionPriceConfig) return 0;
    const { price, minAmount } = collectionPriceConfig;
    // Charge applies when order total is LESS than the minimum free-collection amount
    return baseTotal < minAmount ? price : 0;
  }, [formData.homeCollection, collectionPriceConfig, baseTotal]);

  useEffect(() => {
    onCollectionChargeChange?.(collectionCharge);
  }, [collectionCharge, onCollectionChargeChange]);

  /* ───────── default date when switching to home ───────── */
  useEffect(() => {
    try {
      if (formData.homeCollection && !formData.homeCollectionDate) {
        onFieldChange("homeCollectionDate", TODAY);
      }
    } catch (err) {
      console.error("Home date default error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.homeCollection]);

  /* ───────── fetch HOME slots by date ───────── */
  useEffect(() => {
    const fetchSlots = async () => {
      try {
        if (!formData.homeCollection) {
          setHomeSlots([]);
          return;
        }

        const date = formData.homeCollectionDate || TODAY;
        setSlotsLoading(true);

        const res = await fetch(
          `${API_BASE_URL}/api/slots/date?date=${encodeURIComponent(date)}`,
          { headers: authHeaders() },
        );

        const json = await res.json().catch(() => null);

        if (!res.ok || !json) {
          setHomeSlots([]);
          return;
        }

        const slots = Array.isArray(json?.slots) ? json.slots : [];
        slots.sort((a, b) =>
          String(a.startTimeLabel || "").localeCompare(
            String(b.startTimeLabel || ""),
          ),
        );
        setHomeSlots(slots);
      } catch (err) {
        console.error("Slots fetch error:", err);
        setHomeSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };

    fetchSlots();
  }, [formData.homeCollection, formData.homeCollectionDate]);

  /* ───────── CHANGE 2 – fetch CENTER slots when center + date changes ───────── */
  useEffect(() => {
    const fetchCenterSlots = async () => {
      if (!formData.officeCollection || !formData.collectionCenterId) {
        setCenterSlots([]);
        return;
      }

      try {
        setCenterSlotsLoading(true);
        // Use the B2B-specific visit date
        const date = centerVisitDate || TODAY;

        // ✅ Fetch all active slots for the center on the given date
        // (no categoryId filter — returns all category + global slots)
        const params = new URLSearchParams({ includeGlobal: "true", date });
        const res = await fetch(
          `${API_BASE_URL}/api/centers/${formData.collectionCenterId}/slots?${params.toString()}`,
          { headers: authHeaders() },
        );

        if (!res.ok) {
          setCenterSlots([]);
          return;
        }

        const json = await res.json().catch(() => null);
        // API shape: { date, dayOfWeek, slots: [...] }
        const raw = Array.isArray(json?.slots)
          ? json.slots
          : Array.isArray(json)
          ? json
          : [];

        // Only non-full active slots
        const active = raw.filter((s) => s.isActive !== false && s.isFull !== true);
        active.sort((a, b) =>
          String(a.startTime || "").localeCompare(String(b.startTime || "")),
        );
        setCenterSlots(active);
      } catch (err) {
        console.error("Center slots fetch error:", err);
        setCenterSlots([]);
      } finally {
        setCenterSlotsLoading(false);
      }
    };

    fetchCenterSlots();
  }, [
    formData.officeCollection,
    formData.collectionCenterId,
    centerVisitDate,        // ✅ re-fetch when B2B date changes
  ]);

  /* ───────── clear center slot when center changes ───────── */
  useEffect(() => {
    onFieldChange("centerSlotId", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.collectionCenterId]);

  /* ───────── derived ───────── */
  const chargeWaived = collectionPriceConfig && collectionCharge === 0;
  const chargeApplied = collectionCharge > 0;

  /* ═══════════════════════════════ RENDER ═══════════════════════════════ */
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-200 px-4 py-2 border-b">
        <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          Sample Collection Method
        </h2>
      </div>

      <div className="p-3">
        {/* ── 2-in-1 row: Home + B2B ── */}
        <div className="grid grid-cols-12 gap-3 mb-3 items-stretch">
          {/* Home radio card */}
          <div className="col-span-12 md:col-span-6">
            <label
              className={`h-full p-3 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                formData.homeCollection
                  ? "border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm"
                  : "border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`p-1.5 rounded ${
                    formData.homeCollection
                      ? "bg-gradient-to-r from-blue-100 to-indigo-100"
                      : "bg-gray-100"
                  }`}
                >
                  <Home
                    className={`w-4 h-4 ${
                      formData.homeCollection ? "text-blue-600" : "text-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <div className="font-bold text-gray-800 text-xs">
                    Home Collection
                  </div>
                  <div className="text-[10px] text-gray-600">
                    Collect sample at patient address
                  </div>
                </div>
              </div>

              <input
                type="radio"
                name="collectionType"
                checked={formData.homeCollection}
                onChange={() => {
                  try {
                    onFieldChange("homeCollection", true);
                    onFieldChange("officeCollection", false);
                    onFieldChange(
                      "homeCollectionDate",
                      formData.homeCollectionDate || TODAY,
                    );
                    onFieldChange("collectionCenterId", "");
                    onFieldChange("centerSlotId", "");
                    onFieldChange("slotId", "");
                  } catch (err) {
                    console.error("Home toggle error:", err);
                  }
                }}
                className="w-3 h-3 text-blue-600"
              />
            </label>
          </div>

          {/* B2B radio card */}
          <div className="col-span-12 md:col-span-6">
            <label
              className={`h-full p-3 border rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                formData.officeCollection
                  ? "border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm"
                  : "border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`p-1.5 rounded ${
                    formData.officeCollection
                      ? "bg-gradient-to-r from-blue-100 to-indigo-100"
                      : "bg-gray-100"
                  }`}
                >
                  <Building2
                    className={`w-4 h-4 ${
                      formData.officeCollection
                        ? "text-blue-600"
                        : "text-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <div className="font-bold text-gray-800 text-xs">
                    B2B Center Collection
                  </div>
                  <div className="text-[10px] text-gray-600">
                    Patient visits collection center
                  </div>
                </div>
              </div>

              <input
                type="radio"
                name="collectionType"
                checked={formData.officeCollection}
                onChange={() => {
                  try {
                    onFieldChange("officeCollection", true);
                    onFieldChange("homeCollection", false);
                    onFieldChange("selectedAddressId", "");
                    onFieldChange("slotId", "");
                    onFieldChange("homeCollectionDate", "");
                    onFieldChange("centerSlotId", "");
                  } catch (err) {
                    console.error("B2B toggle error:", err);
                  }
                }}
                className="w-3 h-3 text-blue-600"
              />
            </label>
          </div>
        </div>

        {/* ══════════════ HOME COLLECTION UI ══════════════ */}
        {formData.homeCollection && (
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-800">
                Home Collection Details
              </h3>

              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={onOpenAddressSelect}
                  className="text-[10px] text-blue-700 hover:text-blue-800 font-semibold flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                >
                  <ChevronRight className="w-3 h-3" /> Change
                </button>

                <button
                  type="button"
                  onClick={onOpenNewAddress}
                  className="text-[10px] text-blue-700 hover:text-blue-800 font-semibold flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>

            {/* Address */}
            {formData.selectedAddressId ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-2 text-xs mb-2">
                {selectedAddress ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span className="font-bold text-blue-800 text-xs">
                        {selectedAddress.saveas || "Address"}
                        {selectedAddress.isDefault && (
                          <span className="ml-1.5 text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                            Default
                          </span>
                        )}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-700 pl-5">
                      {formatAddress(selectedAddress)}
                    </p>
                  </div>
                ) : (
                  <p className="text-amber-700 text-xs font-medium">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    Selected address not found
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-3 border border-dashed border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg mb-2">
                <AlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                <p className="text-xs font-bold text-yellow-700 mb-1">
                  No address selected
                </p>
                <p className="text-[10px] text-yellow-600 mb-2">
                  Please select an address for home collection
                </p>
                <button
                  type="button"
                  onClick={onOpenAddressSelect}
                  className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-[10px] font-bold rounded hover:from-yellow-600 hover:to-amber-600 transition-all shadow-sm"
                >
                  Select Address
                </button>
              </div>
            )}

            {/* ── CHANGE 1 – Collection Charge Banner ── */}
            {formData.selectedAddressId && (
              <>
                {collectionPriceLoading && (
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 mb-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500" />
                    Checking collection charges for this area…
                  </div>
                )}

                {!collectionPriceLoading && chargeApplied && (
                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-amber-800">
                          Home Collection Charge Applicable
                        </p>
                        <p className="text-[10px] text-amber-700">
                          Free for orders ≥ ₹
                          {collectionPriceConfig?.minAmount?.toLocaleString(
                            "en-IN",
                          )}
                          . Current total is below this.
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-amber-700 whitespace-nowrap ml-2">
                      +₹{collectionCharge.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}

                {!collectionPriceLoading && chargeWaived && (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-green-800">
                        Free Home Collection
                      </p>
                      <p className="text-[10px] text-green-700">
                        Order total qualifies for free home collection (min ₹
                        {collectionPriceConfig?.minAmount?.toLocaleString(
                          "en-IN",
                        )}
                        ).
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Date + Slot */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700">
                  Select Home Collection Date{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  min={TODAY}
                  value={formData.homeCollectionDate || TODAY}
                  onChange={(e) => {
                    try {
                      onFieldChange("homeCollectionDate", e.target.value);
                      onFieldChange("slotId", "");
                    } catch (err) {
                      console.error("Date change error:", err);
                    }
                  }}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Select Slot <span className="text-red-500">*</span>
                </label>

                {slotsLoading ? (
                  <div className="flex items-center justify-center py-2 bg-gray-50 rounded">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    <span className="ml-2 text-xs text-gray-600">
                      Loading slots…
                    </span>
                  </div>
                ) : activeHomeSlots.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    No active slots available for selected date
                  </div>
                ) : (
                  <select
                    required
                    value={formData.slotId || ""}
                    onChange={(e) => {
                      try {
                        onFieldChange("slotId", e.target.value);
                      } catch (err) {
                        console.error("Slot change error:", err);
                      }
                    }}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">— Select Slot —</option>
                    {activeHomeSlots.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {slot.startTimeLabel} – {slot.endTimeLabel}
                        {slot.capacity ? ` (Cap: ${slot.capacity})` : ""}
                        {typeof slot.remaining === "number"
                          ? ` • Rem: ${slot.remaining}`
                          : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ B2B CENTER SELECTION ══════════════ */}
        {formData.officeCollection && (
          <div className="border-t pt-3">
            {/* Row 1 – Center picker only */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700">
                  <Building2 className="w-3 h-3 inline mr-1" />
                  Select Collection Center{" "}
                  <span className="text-gray-400">(optional)</span>
                </label>

                {loading ? (
                  <div className="flex items-center justify-center py-2 bg-gray-50 rounded">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                    <span className="ml-2 text-xs text-gray-600">
                      Loading centers…
                    </span>
                  </div>
                ) : availableCenters.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    No collection centers available.
                  </div>
                ) : (
                  <select
                    value={formData.collectionCenterId || ""}
                    onChange={(e) => {
                      try {
                        onFieldChange("collectionCenterId", e.target.value);
                        onFieldChange("centerSlotId", "");
                      } catch (err) {
                        console.error("Center change error:", err);
                      }
                    }}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">— Select Collection Center —</option>
                    {availableCenters.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}

                <p className="text-[10px] text-gray-500">
                  If not selected, order will be created without B2B center mapping.
                </p>
              </div>
            </div>

            {/* Row 2 – Date + Slot (only when a center is selected) */}
            {formData.collectionCenterId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Visit Date */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Visit Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    min={TODAY}
                    value={centerVisitDate}
                    onChange={(e) => {
                      onFieldChange("centerVisitDate", e.target.value);
                      onFieldChange("centerSlotId", "");
                    }}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                </div>

                {/* Slot picker */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Select Slot{" "}
                    <span className="text-gray-400">(optional)</span>
                  </label>

                  {centerSlotsLoading ? (
                    <div className="flex items-center justify-center py-2 bg-gray-50 rounded">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                      <span className="ml-2 text-xs text-gray-600">
                        Loading slots…
                      </span>
                    </div>
                  ) : centerSlots.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs">
                      <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                      No slots available for this center on selected date.
                    </div>
                  ) : (
                    <select
                      value={formData.centerSlotId || ""}
                      onChange={(e) => {
                        try {
                          onFieldChange("centerSlotId", e.target.value);
                        } catch (err) {
                          console.error("Center slot change error:", err);
                        }
                      }}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">— Select Slot —</option>
                      {centerSlots.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {/* API returns startTime/endTime as "HH:mm" strings */}
                          {slot.startTime} – {slot.endTime}
                          {slot.name ? ` (${slot.name})` : ""}
                          {slot.category?.name ? ` · ${slot.category.name}` : ""}
                          {typeof slot.remaining === "number"
                            ? ` · Rem: ${slot.remaining}`
                            : typeof slot.effectiveCapacity === "number"
                            ? ` · Cap: ${slot.effectiveCapacity}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  )}

                  <p className="text-[10px] text-gray-500">
                    Slots shown for {centerVisitDate}.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SampleCollectionSection;